import { createContext, lazy, memo, Suspense, useContext, useEffect } from "react";
import { LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import type { SidebarView } from "@/components/board-sidebar";
import { BoardsView } from "@/components/boards-view";
import { BoardWindow } from "@/components/board-window";
import { ProjectsView } from "@/components/projects-view";
import { ChangelogView } from "@/components/changelog-view";
import { focusProject } from "@/lib/project-focus";
import { SettingsView } from "@/components/settings-view";
import { ActivityView, QueueView } from "@/components/queue-view";
import { StatusView } from "@/components/status-view";
import { AppsView } from "@/components/apps-view";
import { AgentsView } from "@/components/agents-view";
import { WaitingView } from "@/components/waiting-view";
import { EmptyWindow } from "@/components/empty-window";
import { useWaiting } from "@/lib/waiting-context";
import { focusAgentMessage } from "@/lib/agent-focus";
import { useAuth, useCan } from "@/lib/auth";

/** xterm is big and only the owner opens it: its own chunk, fetched on first use. */
const TerminalView = lazy(() => import("@/components/terminal/terminal-view"));
import type { AgentWithContext, BoardDetail, Project, User } from "@/lib/types";

/** What every view reads from the app, whichever window it is in. */
export interface Shell {
  boards: BoardDetail[];
  archivedBoards: BoardDetail[];
  allBoards: BoardDetail[];
  users: User[];
  projects: Project[];
  agents: AgentWithContext[];
  loading: boolean;
  now: number;
  nav: "sidebar" | "tabs";
  viewportWide: boolean;
  lastBoardId: string | null;
  openTask: (taskId: string) => void;
  refresh: () => void;
  setBoardArchived: (detail: BoardDetail, archived: boolean) => void;
  removeBoard: (detail: BoardDetail) => void;
  createBoard: () => void;
  /** Bumped by the tab layout's top bar; only the window on screen acts on them. */
  newAgentRequest: number;
  newProjectRequest: number;
  newTaskRequest: number;
  connectRequest: number;
}

export const ShellContext = createContext<Shell | null>(null);

/** `instead`: the view this one replaces because it cannot be shown — its tab moves on, not the one on screen. */
export interface GoOptions {
  instead?: SidebarView;
}
/** A window's navigation: a link inside it opens there (a new tab on the desktop). */
export type Go = (view: SidebarView, options?: GoOptions) => void;
const useShell = (): Shell => {
  const shell = useContext(ShellContext);
  if (!shell) throw new Error("ViewPane outside ShellContext");
  return shell;
};

/** Where back ends up: the first tab, and the view a fresh session opens on. */
export const HOME_VIEW: SidebarView = { kind: "status" };

/** A view's name, for the tab layout's top bar and a window's title bar. */
export function viewTitle(view: SidebarView, boards: BoardDetail[], agents: AgentWithContext[]): string {
  switch (view.kind) {
    case "board":
      return boards.find((detail) => detail.board.id === view.boardId)?.board.name ?? "Board";
    case "agents": {
      const agent = view.agentId ? agents.find((candidate) => candidate.id === view.agentId) : null;
      return agent ? agent.name : "Agents";
    }
    case "queue":
      return "My tasks";
    case "activity":
      return "Activity";
    case "status":
      return "Status";
    case "apps":
      return "Apps";
    case "boards":
      return "Boards";
    case "waiting":
      return "Awaiting your answer";
    case "projects":
      return "Projects";
    case "changelog":
      return "Changelog";
    case "terminal":
      return "Terminal";
    case "empty":
      return "New window";
    default:
      return "Settings";
  }
}

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-0 flex-1 overflow-y-auto scrollbar-slim">{children}</div>
);

/**
 * One view, for one window — or for the whole screen in the tab layout, which is
 * the focused window with no chrome. Every link inside goes through `go`, which
 * is the window's own: a board opened from a chat opens in the chat's window.
 */
export const ViewPane = memo(function ViewPane(props: ViewPaneProps) {
  // The box the `win-*` variants measure (index.css): a view lays itself out
  // by the window it is in, not by the screen.
  return (
    <div className="@container/win flex min-h-0 min-w-0 flex-1 flex-col">
      <ViewBody {...props} />
    </div>
  );
}, samePane);

type ViewPaneProps = Parameters<typeof ViewBody>[0];

/**
 * A pane re-renders when what it shows changes, or what it reads from the shell —
 * not whenever the workspace around it does (a focus change, a drag step, another
 * window's tab). The view is compared by value: the workspace rebuilds its leaves.
 */
function samePane(a: ViewPaneProps, b: ViewPaneProps): boolean {
  return (
    a.go === b.go &&
    a.windowed === b.windowed &&
    a.onScreen === b.onScreen &&
    (a.shownAgentId ?? null) === (b.shownAgentId ?? null) &&
    (a.view === b.view || JSON.stringify(a.view) === JSON.stringify(b.view))
  );
}

function ViewBody({
  view,
  go,
  windowed,
  onScreen,
  shownAgentId = null,
}: {
  view: SidebarView;
  go: Go;
  /** In a window frame of the desktop workspace, rather than the whole screen. */
  windowed: boolean;
  /** The one the tab layout shows: the top bar's + requests are meant for it. */
  onScreen: boolean;
  /** An Agents list window's: the agent whose chat window sits beside it, marked in the list. */
  shownAgentId?: string | null;
}) {
  const shell = useShell();
  const waiting = useWaiting();
  const { user: currentUser } = useAuth();
  const canSeeAgents = useCan("admin");
  const isOwner = useCan("owner");
  const { boards, allBoards, users, projects, agents, now, nav, loading } = shell;
  const showTitle = nav !== "tabs";
  const openAgent = (agentId: string, messageId?: string | null) => {
    if (messageId) focusAgentMessage(agentId, messageId);
    go({ kind: "agents", agentId });
  };

  // A board that disappears — deleted, not archived: an archived board is still
  // openable from the archive — sends its window somewhere sensible.
  const boardMissing = view.kind === "board" && !loading && !allBoards.some((detail) => detail.board.id === view.boardId);
  useEffect(() => {
    if (!boardMissing) return;
    go(nav === "tabs" ? { kind: "boards" } : boards[0] ? { kind: "board", boardId: boards[0].board.id } : { kind: "queue" }, { instead: view });
  }, [boardMissing, boards, nav, go]);
  // Only an admin has the Awaiting view; a stored or shortcut one for anyone else goes home.
  useEffect(() => {
    if (view.kind === "waiting" && !waiting.enabled) go(HOME_VIEW, { instead: view });
  }, [view.kind, waiting.enabled, go]);
  // The terminal is the owner's alone; a stored, shared or shortcut one for anyone else goes home.
  useEffect(() => {
    if (view.kind === "terminal" && !isOwner) go(HOME_VIEW, { instead: view });
  }, [view.kind, isOwner, go]);

  // Only the views drawn from the boards wait for them; the rest load their own.
  if (loading && (view.kind === "board" || view.kind === "boards" || view.kind === "archive")) {
    return (
      <div className="space-y-3 px-4 py-3 win-sm:px-6 win-sm:py-4">
        <Skeleton className="h-8 w-64" />
        <div className="flex gap-3">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-72 w-72" />
          ))}
        </div>
      </div>
    );
  }

  switch (view.kind) {
    case "empty":
      // Only the desktop workspace makes one; a stored one in the tab layout shows Status.
      if (windowed) return <EmptyWindow boards={boards} agents={agents} canAwait={waiting.enabled} canSeeAgents={canSeeAgents} go={go} />;
      return (
        <Scroll>
          <StatusView
            users={users}
            onOpenAgents={() => go({ kind: "agents" })}
            onOpenAgent={(agentId) => go({ kind: "agents", agentId })}
            onOpenTask={shell.openTask}
            showTitle={showTitle}
          />
        </Scroll>
      );
    case "queue":
      return (
        <Scroll>
          <QueueView assignee={currentUser.id} users={users} now={now} onOpenTask={shell.openTask} showTitle={showTitle} />
        </Scroll>
      );
    case "status":
      return (
        <Scroll>
          <StatusView
            users={users}
            onOpenAgents={() => go({ kind: "agents" })}
            onOpenAgent={(agentId) => go({ kind: "agents", agentId })}
            onOpenTask={shell.openTask}
            showTitle={showTitle}
          />
        </Scroll>
      );
    case "waiting":
      return (
        <Scroll>
          <WaitingView
            cards={waiting.cards}
            questions={waiting.questions}
            reviews={waiting.reviews}
            onChanged={() => void waiting.refresh()}
            loaded={waiting.loaded}
            agents={agents}
            users={users}
            now={now}
            onOpenTask={shell.openTask}
            onOpenAgent={openAgent}
            showTitle={showTitle}
          />
        </Scroll>
      );
    case "projects":
      return (
        <Scroll>
          <ProjectsView
            projects={projects}
            users={users}
            showTitle={showTitle}
            addRequest={onScreen ? shell.newProjectRequest : 0}
            onChanged={shell.refresh}
            onOpenBoard={(boardId) => go({ kind: "board", boardId })}
            onOpenTask={shell.openTask}
            onOpenAgent={(agentId) => go({ kind: "agents", agentId })}
          />
        </Scroll>
      );
    case "changelog":
      return (
        <Scroll>
          <ChangelogView showTitle={showTitle} />
        </Scroll>
      );
    case "terminal":
      if (!isOwner) return null;
      return (
        <Suspense
          fallback={
            <div className="grid flex-1 place-items-center text-[13px] text-muted-foreground">Loading the terminal…</div>
          }
        >
          <TerminalView showTitle={showTitle} />
        </Suspense>
      );
    case "apps":
      return (
        <Scroll>
          <AppsView showTitle={showTitle} />
        </Scroll>
      );
    case "activity":
      return (
        <Scroll>
          <ActivityView users={users} selfId={currentUser.id} boardIds={boards.map((detail) => detail.board.id)} showTitle={showTitle} />
        </Scroll>
      );
    case "agents":
      return (
        <AgentsView
          projects={projects}
          users={users}
          now={now}
          selectedId={view.agentId ?? null}
          // On the desktop the list and a chat are separate windows (App's `go` opens
          // one beside the other), so two chats never each carry a copy of the list.
          pane={windowed ? (view.agentId ? "chat" : "list") : "both"}
          activeId={windowed && !view.agentId ? shownAgentId : undefined}
          onSelect={(agentId) => go({ kind: "agents", agentId: agentId ?? undefined })}
          onManageProjects={() => go({ kind: "projects" })}
          onOpenProject={(projectId) => {
            focusProject(projectId);
            go({ kind: "projects" });
          }}
          onOpenTask={shell.openTask}
          boards={boards}
          onOpenBoard={(boardId) => go({ kind: "board", boardId })}
          newAgentRequest={onScreen ? shell.newAgentRequest : 0}
          showTitle={showTitle}
        />
      );
    case "boards":
      return (
        <Scroll>
          <BoardsView boards={boards} now={now} currentBoardId={shell.lastBoardId} onOpen={(boardId) => go({ kind: "board", boardId })} />
        </Scroll>
      );
    case "settings":
    case "archive":
      return (
        <Scroll>
          <SettingsView
            archivedBoards={shell.archivedBoards}
            now={now}
            showTitle={showTitle}
            onOpenBoard={(boardId) => go({ kind: "board", boardId })}
            onRestoreBoard={(detail) => shell.setBoardArchived(detail, false)}
            onDeleteBoard={shell.removeBoard}
          />
        </Scroll>
      );
    case "board": {
      const detail = allBoards.find((candidate) => candidate.board.id === view.boardId);
      if (detail) {
        return (
          <BoardWindow
            // A switch of board starts the board's own state afresh — its filter, its
            // drag, and any of its dialogs (a chat belongs to one board).
            key={detail.board.id}
            detail={detail}
            allBoards={allBoards}
            users={users}
            projects={projects}
            now={now}
            viewportWide={shell.viewportWide}
            windowed={windowed}
            titleInTopBar={nav === "tabs"}
            newTaskRequest={onScreen ? shell.newTaskRequest : 0}
            connectRequest={onScreen ? shell.connectRequest : 0}
            onOpenTask={shell.openTask}
            onSetArchived={shell.setBoardArchived}
            onDelete={shell.removeBoard}
            onManageProjects={() => go({ kind: "projects" })}
            onChanged={shell.refresh}
          />
        );
      }
      return (
        <div className="grid flex-1 place-items-center p-6">
          <EmptyState
            icon={<LayoutGrid />}
            title="No board selected"
            hint={nav === "tabs" ? "Create a time-boxed board, or pick one under Boards." : "Create a time-boxed board, or pick one from the navigation."}
            action={
              <Button size="sm" className="mt-1" onClick={shell.createBoard}>
                New board
              </Button>
            }
          />
        </div>
      );
    }
  }
}
